'use client';
import React, { FC } from 'react';
import { useSearchParams } from 'next/navigation';

import { TypeImage, TypeVideo } from '@/core/generated-types';
import { getVariantId } from '@/utils/helpers';
import { VariantCircles } from '@/components/common/ProductAddToCart/VariantCircles';
import { Button, Badge, LuvImage, Modal } from '@/components/ui';

import { CarouselProducts } from '../Carousel';
import { LearnMoreModalProps } from './types';

const LearnMoreModal: FC<LearnMoreModalProps> = ({
  isOpen,
  handleClose,
  title,
  image,
  description,
  price,
  originalPrice,
  maxPrice,
  loading,
  buttonText,
  badge,
  buttonColor,
  handleClick,
  product,
  bundleImages,
  selectedVariant,
  setSelectedVariant,
  isBundleUnavailable,
  outOfStockButtonText,
}) => {
  const variants = (product && product.fields?.shopifyProduct?.variants) ?? [];
  const variantImages = product && product.fields?.variantImageSets;
  const shouldShowVariantCircles =
    variants && variants.length > 0 && selectedVariant;

  const searchParams = useSearchParams();
  const defaultVariantId = getVariantId(variants?.[0]);
  const variantId = searchParams?.get('variant') || defaultVariantId;
  const isVariantInStock =
    !variants.length || selectedVariant?.availableForSale;
  const isAvailableForSale = !isBundleUnavailable && isVariantInStock;

  const carouselImages =
    shouldShowVariantCircles && variantImages
      ? variantImages.find(
          (imageSet) => imageSet.fields.variantId === variantId,
        )?.fields.images
      : bundleImages;

  const pricingCopy = maxPrice ? `${price} - ${maxPrice}` : `${price}`;

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      modalClasses={{
        mobile: {
          top: 'auto',
          bottom: '0',
          minWidth: '100vw',
          maxHeight: '90dvh',
          transform: 'translateX(-50%)',
        },
      }}
      overlayClasses={{
        backgroundColor: 'rgba(59,59,59,.75)',
      }}
    >
      <div className="learn-more container lg:gap-6 max-w-sm lg:grid lg:max-w-[936px] lg:grid-cols-2 lg:pt-6 lg:pb-4">
        {carouselImages ? (
          <div className="h-[570px]">
            <CarouselProducts
              id="learn more carousel"
              components={carouselImages as (TypeVideo | TypeImage)[]}
              key={variantId}
            />
          </div>
        ) : (
          <LuvImage
            imageData={image}
            alt={image?.fields?.altText || ''}
            width={456}
            height={305}
          />
        )}

        <div>
          {badge && (
            <div className="mb-3">
              <Badge {...badge.fields} />
            </div>
          )}
          <div className="mb-2 text-2xl lg:text-3xl lg:text-[32px] font-bold">
            <p>{title}</p>
          </div>
          <div className="text-base">{description}</div>
          <div className="flex mt-4 items-baseline gap-x-2">
            <p className="text-h2 font-bold text-red-v2">{pricingCopy}</p>
            <p className="text-h3 text-grey-light line-through">
              {originalPrice}
            </p>
          </div>
          {shouldShowVariantCircles && product && (
            <div className="mt-3 border-t border-grey-midtone pt-5">
              <VariantCircles
                variants={variants}
                selectedVariant={selectedVariant}
                cb={setSelectedVariant}
                productVariantsCopy={null}
                product={product}
              />
            </div>
          )}
          <div className="mt-3 border-t border-grey-midtone pt-5">
            <Button
              fullWidth
              ctaColor={buttonColor}
              onClick={handleClick}
              ctaText={!isAvailableForSale ? outOfStockButtonText : buttonText}
              disabled={loading || !isAvailableForSale}
              includeSpinner={loading}
              classes="disabled:bg-grey-dark disabled:border-grey-dark disabled:opacity-100"
              testId="cta"
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LearnMoreModal;
